import { React, useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'

import Box from '@mui/material/Box'
import Icon from '@mui/material/Icon'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import CardMedia from '@mui/material/CardMedia'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxInput from 'components/custom/AdxInput'
import AdxPopper from 'components/reusable/AdxPopper'
import AdxSortTable from 'components/reusable/table/AdxSortTable'
import AdxToolTip from 'components/custom/AdxToolTip'

import {
  exportActiveStaffEmployeeFile,
  getAllStaffEmployees,
  getUninvitedStaffData,
} from 'services/api/modules/staff-management/staffManagementDashboard'

import defaultImage from 'assets/images/default_avatar.png'
import { Add, AddCircleOutlineOutlined } from '@mui/icons-material'
import AdxButton from 'components/custom/AdxButton'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import { downloadResponseFile, notifyDownloadError } from 'services/helper/fileDownload'

const EmployeeFile = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const inputEl = useRef('')
  const [selectValue, setSelectValue] = useState('All Staff')
  const [exportingFormat, setExportingFormat] = useState(null)
  const lowestOfAll = 'ZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZZ888'
  const isExporting = Boolean(exportingFormat)

  const handleSetSelectValue = (event) => {
    setSelectValue(event.target.value)
  }

  const { isLoading, data } = useQuery('staffListData', async () => {
    const { data } = await getAllStaffEmployees()
    return data
  })
  const { data: uninvitedStaffData } = useQuery('getUninvitedStaff', async () => {
    const { data } = await getUninvitedStaffData()
    return data
  })

  const employeeColumn = [
    {
      Header: 'Staff Name',
      accessor: 'name',
      Cell: (props) => {
        const { value } = props
        return (
          <Box display="flex" alignItems="center" justifyContent="flex-start">
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                  marginRight: 2,
                }}
                src={props?.row?.original?.profile_image_url || defaultImage}
              />
              {props?.row?.original?.employment_status === 2 ? (
                <AdxToolTip title="Terminated employee">
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -10,
                      right: 10,
                    }}
                  >
                    <Icon
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 20,
                        backgroundColor: 'white.main',
                        color: 'error.main',
                        fontSize: 20,
                      }}
                    >
                      error
                    </Icon>
                  </Box>
                </AdxToolTip>
              ) : (
                props?.row?.original?.payroll_status === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -10,
                      right: 10,
                    }}
                  >
                    <AdxToolTip title="Incomplete Employee Details">
                      <Icon
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 20,
                          backgroundColor: 'white.main',
                          fontSize: 20,
                          color: 'warning.main',
                        }}
                      >
                        error
                      </Icon>
                    </AdxToolTip>
                  </Box>
                )
              )}
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
              alignItems="flex-start"
            >
              <Typography>{value}</Typography>
              <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                {props?.row?.original?.designation}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'E-mail',
      accessor: 'work_email',
      Cell: (props) => {
        const { value } = props
        return (
          <Typography variant="body1" color="dark-text.main" align="left" noWrap>
            {value}
          </Typography>
        )
      },
    },
    {
      Header: 'Reporting Manager',
      accessor: 'manager_name',
      Cell: (props) => {
        const { value, row } = props
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 2,
            }}
          >
            {value && value !== lowestOfAll && (
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
                // src={row?.original?.reporting_to?.profile_image_url ? row?.original?.reporting_to?.profile_image_url : defaultImage}
                src={row?.original?.manager_image ? row?.original?.manager_image : defaultImage}
              />
            )}
            <Box sx={{ width: 'calc(100% - 40px)', textAlign: 'start' }}>
              <Typography>{value && value !== lowestOfAll && value}</Typography>
            </Box>
          </Box>
        )
      },
    },
  ]

  const [employeeData, setEmployeeData] = useState([])

  useEffect(() => {
    let tempData = []

    switch (selectValue) {
      case 'All Staff':
        // eslint-disable-next-line no-lone-blocks
        {
          Object.keys(data?.staff || []).forEach((dept) => {
            tempData.push(...data.staff[dept])
          })
        }

        break

      default:
        // eslint-disable-next-line no-lone-blocks
        {
          tempData.push(...data.staff[selectValue])
        }
        break
    }
    setEmployeeData(tempData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectValue])

  useEffect(() => {
    if (data) {
      let tempData = []

      Object.keys(data?.staff || []).forEach((dept) => {
        tempData.push(...data.staff[dept])
      })
      let destructuredData = tempData.map((item) => {
        return {
          ...item,
          manager_name: item?.reporting_to?.name ?? lowestOfAll,
          manager_image: item?.reporting_to?.profile_image_url ?? null,
        }
      })

      setEmployeeData(destructuredData)
    }
  }, [data])

  // filter active/terminated employees for tab feature
  const filterCurrent = (array) => {
    const newArrayList = array.filter((arr) => {
      return arr.employment_status === 1
    })
    return newArrayList
  }

  const filterTerminated = (array) => {
    const newArrayList = array.filter((arr) => {
      return arr.employment_status === 2
    })
    return newArrayList
  }

  // search related with active/terminated filtering twist
  const [searchTerm, setSearchTerm] = useState('')
  const [searchActive, setsearchActive] = useState([])
  const [searchTerminated, setSearchTerminated] = useState([])

  const getSearchTerm = (e) => {
    setSearchTerm(e.target.value)
    if (searchTerm !== '') {
      const newArrayList = employeeData.filter((arr) => {
        return Object.values(arr).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      })
      setsearchActive(filterCurrent(newArrayList))
      setSearchTerminated(filterTerminated(newArrayList))
    } else {
      setsearchActive(filterCurrent(employeeData))
      setSearchTerminated(filterTerminated(employeeData))
    }
  }

  const tableFunction = (staff) => {
    navigate(`/app/management-centre/staff-management/employee-file/${staff.id}`)
  }

  const handleInviteStaff = () => {
    navigate('/app/management-centre/staff-management/employee-file/invite-staff', {
      state: {
        backgroundLocation: location,
      },
    })
  }

  const handleInviteMultipleStaff = () => {
    navigate('/app/management-centre/staff-management/employee-file/invite-multiple-staff', {
      state: {
        backgroundLocation: location,
      },
    })
  }

  const handleReinviteStaff = () => {
    navigate('/app/management-centre/staff-management/employee-file/reinvite-staff', {
      state: {
        backgroundLocation: location,
      },
    })
  }

  const handleExportActiveStaff = async (format) => {
    const exportConfig = {
      xlsx: {
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        fileName: 'active-staff-employee-file.xlsx',
        successMessage: 'Active staff export downloaded.',
        errorMessage: 'Unable to export active staff. Please try again.',
      },
    }
    const config = exportConfig[format]

    try {
      setExportingFormat(format)

      const response = await exportActiveStaffEmployeeFile(format)

      downloadResponseFile(response, {
        fileName: config.fileName,
        contentType: config.contentType,
        successMessage: config.successMessage,
      })
    } catch (error) {
      notifyDownloadError(config.errorMessage)
    } finally {
      setExportingFormat(null)
    }
  }

  const exportMenu = (
    <AdxPopper
      placement="bottom-end"
      disabled={isExporting}
      buttonIcon="more_vert"
      sx={{ minWidth: 180, boxShadow: '0px 0px 10px lightgray' }}
      customButton={
        <AdxToolTip title="Export staff">
          <span>
            <IconButton
              aria-label="Export staff"
              size="small"
              disabled={isExporting}
              sx={{
                color: 'light-gray-text.main',
                '&:hover': {
                  bgcolor: 'gray-background.main',
                },
              }}
            >
              {isExporting ? (
                <CircularProgress color="alternate" size={18} />
              ) : (
                <Icon>more_vert</Icon>
              )}
            </IconButton>
          </span>
        </AdxToolTip>
      }
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        onClick={isExporting ? undefined : () => handleExportActiveStaff('xlsx')}
      >
        <Icon sx={{ color: 'alternate.main', fontSize: 20 }}>download</Icon>
        <Typography>{exportingFormat === 'xlsx' ? 'Exporting XLSX...' : 'Export as XLSX'}</Typography>
      </Box>
    </AdxPopper>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh - 200px',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1,
          pt: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: { xs: '100%', md: '50%' },
          }}
        >
          <AdxDropdown
            value={selectValue}
            onChange={handleSetSelectValue}
            sx={{
              marginRight: 1,
              minWidth: '130px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white.main',
                outline: 'none',
              },
              '& .MuiSelect-select': {
                color: 'dark-text.main',
                paddingRight: 0,
              },
              '& .MuiSvgIcon-root': {
                color: 'light-gray-text.main',
              },
            }}
          >
            <AdxMenuItem value="All Staff" selectedvalue={selectValue}>
              All Staff
            </AdxMenuItem>
            {Object.keys(data?.staff || []).map((dept, index) => {
              return (
                <AdxMenuItem key={index} value={dept} selectedvalue={selectValue}>
                  {dept}
                </AdxMenuItem>
              )
            })}
          </AdxDropdown>
          <AdxInput
            sx={{
              '& .MuiInputBase-root': {
                outline: 'none',
                bgcolor: 'white.main',
              },
            }}
            value={searchTerm}
            ref={inputEl}
            onChange={getSearchTerm}
            startAdornment={
              <InputAdornment position="start">
                <Icon>search</Icon>
              </InputAdornment>
            }
            placeholder="Search staff, department, etc..."
          />
        </Box>
        <AdxPopper
          placement="bottom-end"
          customButton={
            <AdxButton>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: { sm: '100%', md: 'fit-content' },
                }}
              >
                <Add />
                <Typography>Invite Staff</Typography>
              </Box>
            </AdxButton>
          }
        >
          <Box sx={{ display: 'flex' }} onClick={handleInviteStaff}>
            <Icon sx={{ color: 'alternate.main', mr: 1, fontSize: 20 }}>person_add_alt_1</Icon>
            <Typography>Invite a staff</Typography>
          </Box>
          <Box sx={{ display: 'flex' }} onClick={handleInviteMultipleStaff}>
            <Icon sx={{ color: 'alternate.main', mr: 1, fontSize: 20 }}>group_add</Icon>
            <Typography>Invite multiple staffs</Typography>
          </Box>
        </AdxPopper>
      </Box>
      {/* NEW FEATURE RE INVITE */}
      {uninvitedStaffData && (
        <Box
          sx={{
            width: 1,
            height: 50,
            backgroundColor: 'light-yellow-01.main',
            borderRadius: '4.5px',
            display: 'flex',
            flexDirection: 'row',
            columnGap: 1,
            alignItems: 'center',
            borderLeft: '5px solid',
            borderColor: 'yellow-01.main',
            px: 2,
          }}
        >
          <Icon
            sx={{
              color: 'warning.main',
            }}
          >
            error
          </Icon>
          <Typography>
            {' '}
            {uninvitedStaffData.length} {uninvitedStaffData.length > 1 ? 'users' : 'user'} haven't
            signed up yet.
          </Typography>
          <Box onClick={handleReinviteStaff}>
            <Typography
              color="alternate.main"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Resend invitation
            </Typography>
          </Box>
        </Box>
      )}

      {isLoading && (
        <Box
          position="fixed"
          zIndex={20}
          top="50%"
          left="55%"
          sx={{ transform: 'translate(50%, -50%)' }}
        >
          <CircularProgress color="alternate" size={60} />
        </Box>
      )}
      {!isLoading && (
        <Box
          className="scrollbar"
          sx={{
            width: '100%',
            overflow: 'auto',
            bgcolor: 'white.main',
            border: 1,
            borderRadius: '4.5px',
            borderColor: 'gray-line.main',
          }}
        >
          <AdxTabsBadge scrollButtons={false} headerAction={exportMenu}>
            <AdxTabsPanel label="Active">
              <AdxSortTable
                column={employeeColumn}
                data={searchTerm.length < 1 ? filterCurrent(employeeData) ?? [] : searchActive}
                nav="Manage"
                onClick={(item, index) => tableFunction(item, index)}
                sxNav
                sxCell
                stickyHeader
                height={'calc(100vh - 318px)'}
                sx={{
                  zIndex: -1,
                }}
              />
            </AdxTabsPanel>
            <AdxTabsPanel label="Terminated">
              <AdxSortTable
                column={employeeColumn}
                data={
                  searchTerm.length < 1 ? filterTerminated(employeeData) ?? [] : searchTerminated
                }
                nav="Manage"
                onClick={(item, index) => tableFunction(item, index)}
                sxNav
                sxCell
                stickyHeader
                height={'calc(100vh - 318px)'}
                sx={{
                  zIndex: -1,
                }}
              />
            </AdxTabsPanel>
          </AdxTabsBadge>
        </Box>
      )}
    </Box>
  )
}

export default EmployeeFile
